import { useCallback, useEffect, useRef, useState } from 'react';
import { AuditEventCategory } from 'types/entity-audit-trail';
import { EntityAuditTrailDescriptor } from './entityAuditTrail';

const PAGE_SIZE = 50;

/**
 * Paged, filterable feed of one entity's audit trail. Same accumulate-on-load-more contract as the
 * generic audit trail; the filters travel as plain query params instead of RSQL.
 */
export function useEntityAuditTrailFeed(trail: EntityAuditTrailDescriptor, entityId: number, enabled: boolean) {
    const [categories, setCategories] = useState<AuditEventCategory[]>([]);
    const [authorIds, setAuthorIds] = useState<number[]>([]);
    const [includeSystem, setIncludeSystem] = useState(false);
    const pageRef = useRef(0);
    const [fetchEntries, { data, isFetching }] = trail.useLazyEntriesQuery();

    const queryArgs = useCallback(
        (page: number) => ({
            id: entityId,
            page,
            pageSize: PAGE_SIZE,
            extraParams: {
                // objectToQueryParams drops empty strings, so an empty selection sends no param.
                categories: categories.join(','),
                authorIds: authorIds.join(','),
                includeSystem: includeSystem ? 'true' : '',
            },
        }),
        [entityId, categories, authorIds, includeSystem]
    );

    useEffect(() => {
        if (!enabled) {
            return;
        }
        pageRef.current = 0;
        void fetchEntries(queryArgs(0));
    }, [enabled, fetchEntries, queryArgs]);

    const loadMore = useCallback(() => {
        pageRef.current += 1;
        void fetchEntries(queryArgs(pageRef.current));
    }, [fetchEntries, queryArgs]);

    const entries = data?.content ?? [];
    // totalElements counts audit rows, while one row renders as 1..n lines; the "N of M events"
    // footer must compare rows to rows, so the shown side counts distinct row ids (`a-<rowId>-<n>`).
    const shownEventCount = new Set(entries.map((entry) => entry.entryId.split('-', 2).join('-'))).size;

    return {
        categories,
        setCategories,
        authorIds,
        setAuthorIds,
        includeSystem,
        setIncludeSystem,
        entries,
        shownEventCount,
        totalElements: data?.totalElements,
        hasMore: data ? !data.last : false,
        isInitialLoading: isFetching && data === undefined,
        isFetchingMore: isFetching && data !== undefined,
        loadMore,
    };
}
